import { palette, radii, shadows, motion } from './foundation.js';

/**
 * Semantic application tokens.
 *
 * These map foundation values to application meaning (surface, border, state,
 * status). Components should prefer these over raw palette references.
 */

export const surface = {
  page: palette.neutral[50], // main canvas behind page content
  subtle: palette.neutral[25], // alternating/secondary panels
  raised: palette.neutral[0], // cards, paper, popovers
  interactive: palette.neutral[0], // clickable tiles/cards
  sunken: palette.neutral[100], // grouped sections within a page
  overlay: palette.neutral[0], // modals, menus
} as const;

export const border = {
  subtle: palette.neutral[200],
  medium: palette.neutral[300],
  standard: palette.neutral[300],
  strong: palette.neutral[400],
} as const;

export const surfaceBorder = {
  subtle: border.subtle,
  standard: border.medium,
  emphasized: border.strong,
} as const;

export const focusRing = {
  color: palette.primary[500],
  width: 2,
  offset: 2,
  style: 'solid',
} as const;

export const card = {
  standard: {
    bgcolor: 'finance.surface.raised',
    border: 1,
    borderColor: 'finance.border.medium',
    borderRadius: `${radii.medium}px`,
    boxShadow: shadows.none,
  },
  interactive: {
    bgcolor: 'finance.surface.raised',
    border: 1,
    borderColor: 'finance.border.medium',
    borderRadius: `${radii.medium}px`,
    boxShadow: shadows.none,
    transition: `border-color ${motion.duration.fast} ${motion.easing.standard}, box-shadow ${motion.duration.fast} ${motion.easing.standard}`,
    '&:hover': {
      borderColor: 'finance.border.strong',
      boxShadow: shadows.surface,
    },
    '&:focus-visible': {
      outline: `${focusRing.width}px ${focusRing.style} ${focusRing.color}`,
      outlineOffset: `${focusRing.offset}px`,
    },
  },
  emphasized: {
    bgcolor: 'finance.surface.raised',
    border: 1,
    borderColor: 'finance.border.medium',
    borderTop: 3,
    borderTopColor: 'finance.border.strong',
    borderRadius: `${radii.medium}px`,
    boxShadow: shadows.none,
  },
  table: {
    bgcolor: 'finance.surface.raised',
    border: 1,
    borderColor: 'finance.border.medium',
    borderRadius: `${radii.medium}px`,
    overflow: 'hidden',
  },
} as const;

export const tableSurface = {
  container: card.table,
  header: {
    bgcolor: 'finance.surface.sunken',
    borderBottom: 1,
    borderColor: 'finance.border.medium',
  },
} as const;

export const text = {
  primary: palette.neutral[800],
  // MUI applies reduced emphasis to several secondary-text consumers (tabs,
  // labels and supporting copy). Neutral 700 preserves WCAG AA after that
  // compositing on both the page and raised light surfaces.
  secondary: palette.neutral[700],
  disabled: palette.neutral[400],
  inverse: palette.neutral[0],
} as const;

export const status = {
  draft: {
    tone: 'neutral',
    light: palette.neutral[100],
    main: palette.neutral[500],
    dark: palette.neutral[700],
  },
  submitted: {
    tone: 'info',
    light: palette.info.light,
    main: palette.info.main,
    dark: palette.info.dark,
  },
  pending: {
    tone: 'warning',
    light: palette.warning.light,
    main: palette.warning.main,
    dark: palette.warning.dark,
  },
  approved: {
    tone: 'success',
    light: palette.success.light,
    main: palette.success.main,
    dark: palette.success.dark,
  },
  posted: {
    tone: 'success',
    light: palette.success.light,
    main: palette.success.main,
    dark: palette.success.dark,
  },
  rejected: {
    tone: 'error',
    light: palette.error.light,
    main: palette.error.main,
    dark: palette.error.dark,
  },
  withdrawn: {
    tone: 'neutral',
    light: palette.neutral[100],
    main: palette.neutral[500],
    dark: palette.neutral[700],
  },
  reversed: {
    tone: 'warning',
    light: palette.warning.light,
    main: palette.warning.main,
    dark: palette.warning.dark,
  },
  failed: {
    tone: 'error',
    light: palette.error.light,
    main: palette.error.main,
    dark: palette.error.dark,
  },
  completed: {
    tone: 'success',
    light: palette.success.light,
    main: palette.success.main,
    dark: palette.success.dark,
  },
  planned: {
    tone: 'info',
    light: palette.info.light,
    main: palette.info.main,
    dark: palette.info.dark,
  },
  disabled: {
    tone: 'neutral',
    light: palette.neutral[100],
    main: palette.neutral[400],
    dark: palette.neutral[600],
  },
  degraded: {
    tone: 'warning',
    light: palette.warning.light,
    main: palette.warning.main,
    dark: palette.warning.dark,
  },
  stale: {
    tone: 'neutral',
    light: palette.neutral[100],
    main: palette.neutral[400],
    dark: palette.neutral[600],
  },
  open: {
    tone: 'error',
    light: palette.error.light,
    main: palette.error.main,
    dark: palette.error.dark,
  },
  closed: {
    tone: 'neutral',
    light: palette.neutral[100],
    main: palette.neutral[500],
    dark: palette.neutral[700],
  },
  partial: {
    tone: 'warning',
    light: palette.warning.light,
    main: palette.warning.main,
    dark: palette.warning.dark,
  },
  success: {
    tone: 'success',
    light: palette.success.light,
    main: palette.success.main,
    dark: palette.success.dark,
  },
  enabled: {
    tone: 'success',
    light: palette.success.light,
    main: palette.success.main,
    dark: palette.success.dark,
  },
  investigating: {
    tone: 'info',
    light: palette.info.light,
    main: palette.info.main,
    dark: palette.info.dark,
  },
  resolved: {
    tone: 'success',
    light: palette.success.light,
    main: palette.success.main,
    dark: palette.success.dark,
  },
  adjusted: {
    tone: 'warning',
    light: palette.warning.light,
    main: palette.warning.main,
    dark: palette.warning.dark,
  },
  ignored: {
    tone: 'neutral',
    light: palette.neutral[100],
    main: palette.neutral[500],
    dark: palette.neutral[700],
  },
} as const;

export const action = {
  primary: { variant: 'contained', color: 'primary' as const },
  secondary: { variant: 'outlined', color: 'primary' as const },
  tertiary: { variant: 'text', color: 'inherit' as const },
  warning: { variant: 'outlined', color: 'warning' as const },
  danger: { variant: 'contained', color: 'error' as const },
} as const;

export const shadow = {
  surface: shadows.surface,
  raised: shadows.raised,
  overlay: shadows.overlay,
} as const;

export const appShadow = {
  header: '0 1px 0 rgba(15, 23, 42, 0.04)',
  cardHover: '0 8px 22px -8px rgba(15, 23, 42, 0.28)',
} as const;

export const radius = {
  small: radii.small,
  medium: radii.medium,
  large: radii.large,
  pill: radii.pill,
} as const;
